/**
 * The verdict: your book today, then every mix, on the same three numbers.
 *
 * ROADMAP "The Strategist result page reads clearly and explains itself",
 * item 3 (owner, 2026-09-06: *"more readable and more intuitive to understand
 * … need more intuitive design and layout"*). Before this, each mix carried
 * its own three-figure grid stacked down one 480px column, so comparing eight
 * of them meant scrolling and holding numbers in your head — and today's book,
 * the thing every option is measured against, sat in a paragraph above them
 * in different words ("returned 10.9% a year" for the same quantity the
 * options called "Yearly return").
 *
 * One row per mix, today's first, one label per quantity, and the delta from
 * today in the column that answers "is changing worth it?". Selecting a row is
 * how the detail beneath follows the reader's attention.
 *
 * A missing figure renders as a dash, never as a zero: today's backtest can
 * legitimately fail to produce a return while its stress numbers stand
 * ([[ROADMAP A.25]]), and a 0.0% would read as "flat", which is a claim.
 */
import React from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { RebalanceComparison, RebalanceOption } from '../../data/rebalanceComparison';
import { typography } from '../../common/styles/universalTheme';
import { ExplainedElement, ExplainedText } from '../MetricTooltip';

const numSx = { fontVariantNumeric: 'tabular-nums lining-nums', fontFamily: 'monospace' as const };
export const pct = (x: number): string => `${(x * 100).toFixed(1)}%`;
const orDash = (x: number | undefined | null, render: (v: number) => string): string =>
  typeof x === 'number' && Number.isFinite(x) ? render(x) : '—';

const HEAD: ReadonlyArray<{ label: string; explain: string }> = [
  { label: 'Mix',
    explain: 'Your book as it stands, and the other ways of dividing the same capital that were tested against it.' },
  { label: 'Yearly return',
    explain: 'Annualised across the whole evidence period. It is what would have happened, not a forecast of what will.' },
  { label: 'Ups & downs',
    explain: 'How much the mix moved year to year. Lower is steadier, which is not the same as safer.' },
  { label: 'Return per unit of risk',
    explain: 'What each unit of movement bought in return. The table is not ordered on it, and it does not always agree with the ordering — which is the reason it is shown.' },
  { label: 'Typical bad case',
    explain: 'The average of the worst half of the shocks tested, rather than the single deepest. One bad episode can be circumstance; the average of the bad half is closer to what to plan around.' },
  { label: 'vs today',
    explain: 'The difference between this mix and your own book on the ranked measure, in percentage points.' },
];

/** A ratio, not a percentage — two decimals, and never through `pct`. */
const ratio = (x: number): string => x.toFixed(2);

/** The reader's question in one cell: how much less does this fall?
 *
 *  Exported because the headline block above the dateline states the same
 *  comparison for the leading mix. Two functions computing one sentence is how
 *  a page comes to disagree with itself — a headline reading "0.6 pts less"
 *  beside a table row reading "0.7" is worse than no headline at all, so there
 *  is exactly one implementation and both callers use it.
 *
 *  `baseline` is passed rather than closed over so the caller need not be
 *  inside this component. An absent baseline is a dash: the book's own tail
 *  was not measured, and inventing a comparison would be a claim.
 */
export function versusBook(
  tail: number,
  baseline: number | undefined,
): { text: string; color: string } {
  if (typeof baseline !== 'number') return { text: '—', color: 'text.disabled' };
  const delta = baseline - tail;
  if (Math.abs(delta) < 0.005) return { text: 'about the same', color: 'text.secondary' };
  const points = Math.abs(delta * 100).toFixed(1);
  return delta > 0
    ? { text: `${points} pts less`, color: 'success.main' }
    : { text: `${points} pts more`, color: 'error.main' };
}

const cellSx = { fontSize: typography.fontSize.sm, borderColor: 'divider', py: 1 } as const;

export const RebalanceVerdictTable: React.FC<{
  comparison: RebalanceComparison;
  selectedIndex: number;
  onSelect: (index: number) => void;
  onSaveOption?: (option: RebalanceOption) => void;
  /** The chosen mix's breakdown, rendered in a row directly beneath it.
   *
   *  A render-prop rather than a component: the breakdown needs the book's
   *  baseline figures, which live in the card. Passing them through this table
   *  would drag four more props across a boundary that has no use for them. */
  renderDetail?: (index: number) => React.ReactNode;
}> = ({ comparison, selectedIndex, onSelect, onSaveOption, renderDetail }) => {
  const baseline = comparison.current?.tail_stress_drawdown;

  /* Computed, never a literal. The save column exists only when the caller
     offers saving, so a hardcoded number is right in one of the two cases and
     silently breaks the detail row's width in the other. */
  const columnCount = HEAD.length + (onSaveOption ? 1 : 0);

  /* The highest return-per-unit-of-risk among the options, or null when no
     option carries a usable one — in which case nothing is marked rather than
     an arbitrary row being emphasised. */
  const finiteSharpes = comparison.options
    .map((o) => o.sharpe)
    .filter((x): x is number => typeof x === 'number' && Number.isFinite(x));
  const bestSharpe = finiteSharpes.length ? Math.max(...finiteSharpes) : null;

  const versusToday = (tail: number) => versusBook(tail, baseline);

  return (
    // Wide content scrolls inside its own box; the page never scrolls sideways.
    <Box data-testid="verdict-scroller" sx={{ overflowX: 'auto', mb: 2 }}>
      <Table size="small" sx={{ minWidth: 520 }}>
        <TableHead>
          <TableRow>
            {HEAD.map(({ label, explain }, i) => (
              <TableCell key={label} align={i === 0 ? 'left' : 'right'}
                sx={{ ...cellSx, fontWeight: 700, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                <ExplainedText description={explain}>{label}</ExplainedText>
              </TableCell>
            ))}
            {onSaveOption && <TableCell sx={cellSx} />}
          </TableRow>
        </TableHead>
        <TableBody>
          {comparison.current && (
            <TableRow>
              <TableCell sx={{ ...cellSx, fontWeight: 700, color: 'text.primary' }}>Your book today</TableCell>
              <TableCell align="right" sx={{ ...cellSx, ...numSx, color: 'text.primary' }}>
                {orDash(comparison.current.annualized_return, pct)}
              </TableCell>
              <TableCell align="right" sx={{ ...cellSx, ...numSx, color: 'text.primary' }}>
                {orDash(comparison.current.volatility, pct)}
              </TableCell>
              <TableCell align="right" sx={{ ...cellSx, ...numSx, color: 'text.primary' }}>
                {orDash(comparison.current.sharpe, ratio)}
              </TableCell>
              <TableCell align="right" sx={{ ...cellSx, ...numSx, color: 'error.main', fontWeight: 700 }}>
                −{pct(comparison.current.tail_stress_drawdown)}
              </TableCell>
              <TableCell align="right" sx={{ ...cellSx, color: 'text.disabled' }}>the line</TableCell>
              {onSaveOption && <TableCell sx={cellSx} />}
            </TableRow>
          )}

          {comparison.options.map((option, i) => {
            const v = versusToday(option.tail_stress_drawdown);
            const selected = i === selectedIndex;
            return (
              <React.Fragment key={option.strategy}>
              <TableRow
                hover
                onClick={() => onSelect(i)}
                aria-selected={selected}
                aria-expanded={renderDetail ? selected : undefined}
                sx={{
                  cursor: 'pointer',
                  bgcolor: selected ? 'action.selected' : 'transparent',
                }}
              >
                <TableCell sx={{ ...cellSx, color: 'text.primary' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    {/* A row that opens should look like one. Decorative only —
                        the row itself carries the state and the label. */}
                    {renderDetail && (
                      <Box component="span" aria-hidden sx={{
                        fontSize: typography.fontSize.xs, color: 'text.secondary', lineHeight: 1,
                        transform: selected ? 'rotate(90deg)' : 'none',
                        transition: 'transform 120ms',
                        display: 'inline-block',
                      }}>&#9654;</Box>
                    )}
                    <Box component="span" sx={{ fontWeight: selected ? 700 : 400 }}>{option.label}</Box>
                    {i === 0 && (
                      <Typography component="span" sx={{
                        fontSize: typography.fontSize.xs, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em',
                        color: 'background.paper', bgcolor: 'text.primary', px: 0.75, py: 0.25, whiteSpace: 'nowrap',
                      }}>
                        <ExplainedElement description="Shallowest typical bad case of the mixes tested. Steadiest, not highest-returning — the other columns often disagree.">
                          <span>Smallest typical fall</span>
                        </ExplainedElement>
                      </Typography>
                    )}
                    {option.cross_check?.verdict === 'differ' && (
                      <Typography component="span" sx={{
                        fontSize: typography.fontSize.xs, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em',
                        color: 'text.secondary', border: '1px solid', borderColor: 'divider',
                        px: 0.75, py: 0.25, whiteSpace: 'nowrap',
                      }}>
                        <ExplainedElement description="The two ways of judging how these holdings move together ranked this mix differently. Treat its position as uncertain rather than settled.">
                          <span>models differ</span>
                        </ExplainedElement>
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ ...cellSx, ...numSx, color: 'success.main', fontWeight: 700 }}>
                  {pct(option.annualized_return)}
                </TableCell>
                <TableCell align="right" sx={{ ...cellSx, ...numSx, color: 'text.primary' }}>
                  {pct(option.volatility)}
                </TableCell>
                {/* Marked, never ranked on. The leader here is usually NOT the
                    row at the top — the table is ordered on the typical bad
                    case — and a reader who only ever sees one ordering never
                    learns that the ordering was a choice. */}
                <TableCell
                  align="right"
                  sx={{
                    ...cellSx,
                    ...numSx,
                    color: 'text.primary',
                    ...(option.sharpe === bestSharpe
                      ? { fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: '3px' }
                      : {}),
                  }}
                >
                  {ratio(option.sharpe)}
                </TableCell>
                <TableCell align="right" sx={{ ...cellSx, ...numSx, color: 'error.main', fontWeight: 700 }}>
                  −{pct(option.tail_stress_drawdown)}
                </TableCell>
                <TableCell align="right" sx={{ ...cellSx, color: v.color, whiteSpace: 'nowrap' }}>{v.text}</TableCell>
                {onSaveOption && (
                  <TableCell align="right" sx={cellSx}>
                    <Box
                      component="span"
                      role="button"
                      tabIndex={0}
                      onClick={(e: React.MouseEvent) => { e.stopPropagation(); onSaveOption(option); }}
                      onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); onSaveOption(option); }
                      }}
                      sx={{ color: 'text.primary', textDecoration: 'underline', cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >
                      Save this mix
                    </Box>
                  </TableCell>
                )}
              </TableRow>
              {/* Selection and disclosure in one gesture: the mix just clicked
                  opens directly under its own numbers, so the eye never leaves
                  the row it is judging. One at a time — `selectedIndex` already
                  guarantees that, and two open breakdowns would push the table
                  apart until the comparison it exists for is unreadable.

                  It PRINTS: only the selected mix's breakdown has ever been
                  rendered, so paper carries exactly what it always did and no
                  `no-print` belongs here. */}
              {renderDetail && selected && (
                <TableRow data-testid="verdict-detail-row">
                  <TableCell
                    colSpan={columnCount}
                    sx={{ p: 0, borderColor: 'divider', bgcolor: 'action.hover' }}
                  >
                    {renderDetail(i)}
                  </TableCell>
                </TableRow>
              )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

export default RebalanceVerdictTable;
